/*
  Main PDP quantity selector → live price updates.

  Historically the quantity_selector block's <select name="quantity"> only
  changed what got POSTed to /cart/add — every visible price (price block,
  sticky ADD TO CART bar, financing) kept showing single-unit amounts until
  the customer reached the cart. This element makes them all track quantity:

  - Money nodes annotated with data-qty-base (unit price in cents, plus
    optional data-qty-divide / data-qty-suffix / data-qty-minus) inside the
    price block and the sticky bar are rewritten to base × qty on change.
    The annotations live in snippets/single_product_price.liquid and
    snippets/sticky-pdp-menu.liquid.
  - Pages running the bundle/addons pipeline (#Bundle-Savings +
    <addons-quantity>) delegate the subtotal / sticky / compare-at math to
    bundle-item.js, which multiplies the main product's share by
    window.getMainProductQuantity(). This avoids two scripts writing the
    same nodes.
  - Variant swaps re-render the price block with fresh single-unit markup
    (assets/main.js renderProductInfo → innerHTML swap), so a
    MutationObserver re-applies the multiplication right after.
  - snippets/block-financing.liquid listens for the
    `main-product:quantity-change` event dispatched here.
*/
if (!customElements.get('main-product-quantity')) {
  customElements.define(
    'main-product-quantity',
    class MainProductQuantity extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.select = this.querySelector('select[name="quantity"]');
        if (!this.select) return;

        // Read by bundle-item.js (updateBundlePrice / updateCompareAtTotals)
        // and block-financing.liquid so their totals include the main qty.
        window.getMainProductQuantity = () => this.quantity;

        this.select.addEventListener('change', () => this.apply());
        this.observePriceBlock();
        document.addEventListener('variant:swatch', () => this.applySoon());

        this.apply();
      }

      get quantity() {
        const qty = parseInt(this.select?.value, 10);
        return qty > 0 ? qty : 1;
      }

      // The price block is replaced wholesale on variant change — the fresh
      // markup carries the new variant's unit prices in data-qty-base, so we
      // just need to re-run the multiplication over it.
      observePriceBlock() {
        const block = document.getElementById(`price-${this.sectionId}`);
        if (!block) return;
        this.observer = new MutationObserver(() => {
          if (this.applying) return;
          this.applySoon();
        });
        this.observer.observe(block, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      applySoon() {
        Promise.resolve().then(() => this.apply());
      }

      hasBundlePipeline() {
        return !!(
          document.getElementById('Bundle-Savings') &&
          document.querySelector('addons-quantity')
        );
      }

      apply() {
        const qty = this.quantity;
        this.applying = true;

        const priceBlock = document.getElementById(`price-${this.sectionId}`);
        if (priceBlock) {
          priceBlock
            .querySelectorAll('[data-qty-base]')
            .forEach((el) => this.renderMoney(el, qty));
        }

        if (!this.hasBundlePipeline()) {
          // No addons pipeline on this page — this element owns the sticky
          // bar totals. (With the pipeline, bundle-item.js recomputes sticky
          // + compare-at totals itself, triggered by the event below.) The
          // sticky "or $X/mo" financing figures are owned by
          // syncStickyFinancing in sticky-pdp-menu.liquid, which listens for
          // the quantity-change event dispatched below.
          document
            .querySelectorAll('.sticky-price[data-qty-base]')
            .forEach((el) => this.renderMoney(el, qty));

          document
            .querySelectorAll('.sticky-menu-compare-at-price[data-amount]')
            .forEach((el) => {
              const base = parseInt(el.dataset.amount, 10) || 0;
              if (!base || el.classList.contains('hidden')) return;
              const text = this.formatPrice((base * qty) / 100);
              if (el.textContent.trim() !== text) el.textContent = text;
            });
        }

        document.dispatchEvent(
          new CustomEvent('main-product:quantity-change', {
            detail: { quantity: qty },
          })
        );

        // Reset after the MutationObserver microtask has drained so our own
        // writes don't re-trigger apply().
        Promise.resolve().then(() => {
          this.applying = false;
        });
      }

      renderMoney(el, qty) {
        const base = parseInt(el.dataset.qtyBase, 10);
        if (isNaN(base)) return;
        const divide = parseInt(el.dataset.qtyDivide, 10) || 1;
        const cents = Math.round((base * qty) / divide);

        let text = this.formatPrice(cents / 100);
        if (el.hasAttribute('data-qty-minus')) text = `- ${text}`;
        if (el.dataset.qtySuffix) text += el.dataset.qtySuffix;

        // Idempotent write — also guards against observer feedback loops.
        if (el.textContent.trim() !== text) el.textContent = text;
      }

      // Market-aware formatting, mirroring money.liquid the same way
      // price-per-variant.js and bundle-item.js#formatPrice do.
      formatPrice(price) {
        const cartStrings = window.cartStrings;
        if (!cartStrings) {
          return `$${Math.round(price).toLocaleString()}`;
        }

        const currency = {
          country: cartStrings.country,
          code: cartStrings.currency,
          lang: cartStrings.language,
        };

        const currentStore = window.internationalization?.stores?.find(
          (store) => store.shopifyMarket === currency.country
        );
        const language = currentStore?.languages?.find(
          (lang) => lang.lang === currency.lang
        );
        const currencySettings = currentStore?.currency || {
          showIsoCode: false,
          trailingZeros: false,
          prepend: '',
        };

        const isGcc =
          currency.country === 'AE' ||
          currency.country === 'KW' ||
          currency.country === 'SA' ||
          currency.country === 'QA' ||
          currency.country === 'Qatar';

        let formatted;

        if (currency.code === 'EUR') {
          // money.liquid: money_without_trailing_zeros | remove symbol |
          // '.' → ' ' | append ' €'
          const baseAmount = price.toFixed(2).replace(/\.00$/, '');
          let eurAmount = parseFloat(baseAmount).toLocaleString('fr-FR', {
            minimumFractionDigits: baseAmount.includes('.') ? 2 : 0,
            maximumFractionDigits: 2,
          });
          eurAmount = eurAmount
            .replace(/€/g, '')
            .replace(/\./g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          formatted = `${eurAmount} €`;
        } else if (isGcc) {
          const baseFormatted = new Intl.NumberFormat('en', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3,
            useGrouping: true,
          }).format(price);
          if (currency.country === 'QA' || currency.country === 'Qatar') {
            formatted = `QAR ${baseFormatted}`;
          } else if (currency.country === 'SA') {
            formatted = `${baseFormatted} SR`;
          } else {
            // AE and KW both display AED
            formatted = `AED ${baseFormatted}`;
          }
        } else {
          const locale =
            currency.country === 'CA'
              ? 'en-CA'
              : currency.country === 'US'
              ? 'en-US'
              : language?.code || 'en';
          formatted = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency.code,
            trailingZeroDisplay: currencySettings.trailingZeros
              ? 'auto'
              : 'stripIfInteger',
          }).format(price);

          if (currency.country === 'SG' && currencySettings.prepend === 'S') {
            formatted = formatted.replace('$', 'S$');
          } else if (
            currencySettings.prepend &&
            currency.country !== 'SG'
          ) {
            formatted = `${currencySettings.prepend}${formatted}`;
          }
        }

        return formatted.replace(/\s+/g, ' ').trim();
      }
    }
  );
}
